import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { TextButton } from '../buttons/TextButton';

type Props = {
  id?: string;
  title?: string;
  hideActionButton?: boolean;
  isActionDisabled?: boolean;
  actionButtonText?: string;
  actionButtonTitle?: string;
  actionButtonType?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'text-only'
    | 'danger';

  isSecondaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  secondaryButtonTitle?: string;
  onSecondaryButtonClick?: () => void;

  hideCancelButton?: boolean;
  cancelButtonText?: string;

  onClose: () => void;
  onCancelClick?: () => void;
  onActionClick?: () => void;

  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isVerticallyCentered?: boolean;
};

const Modal = ({
  id,
  title,
  hideActionButton = false,
  isActionDisabled,
  actionButtonText = 'Ok',
  actionButtonTitle,
  actionButtonType,
  isSecondaryButtonDisabled,
  secondaryButtonText,
  secondaryButtonTitle,
  onSecondaryButtonClick,

  hideCancelButton = false,
  cancelButtonText = 'Cancel',
  onClose,
  onCancelClick,
  onActionClick,

  children,
  size,
  isVerticallyCentered = false
}: Props) => {
  const ignoreBodyClicks = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const sizeToWidth: Record<NonNullable<Props['size']>, string> = {
    sm: '400px',
    md: '600px',
    lg: '700px',
    xl: '1000px'
  };

  const [container] = useState(() => {
    const div = document.createElement('div');
    div.className = 'modal-container';
    return div;
  });

  useEffect(() => {
    document.body.appendChild(container);
    return () => {
      document.body.removeChild(container);
    };
  }, [container]);

  const [isMouseDownOnBackdrop, setIsMouseDownOnBackdrop] = useState(false);

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only set true if the mousedown is on the backdrop, not the modal
    if (e.target === e.currentTarget) {
      setIsMouseDownOnBackdrop(true);
    }
  };

  const handleBackdropMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if mousedown and mouseup both happened on the backdrop
    if (isMouseDownOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
    setIsMouseDownOnBackdrop(false);
  };

  return ReactDOM.createPortal(
    <div
      id="modal-space"
      data-testid="modal-space"
      className={`${isVerticallyCentered ? 'modal-centered' : ''}`}
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="custom-modal"
        onClick={ignoreBodyClicks}
        id={id}
        data-testid={id}
        style={{
          width: sizeToWidth[size]
        }}
      >
        <div className="modal-title">{title}</div>

        <div className="modal-contents">{children}</div>

        <div
          className="modal-buttons"
          hidden={hideActionButton && hideCancelButton}
        >
          {!hideActionButton && (
            <TextButton
              onClick={onActionClick}
              disabled={isActionDisabled}
              type={actionButtonType || 'primary'}
              text={actionButtonText}
              title={actionButtonTitle}
              id="modal-primary-action-button"
              testId="modal-primary-action-button"
            />
          )}
          {secondaryButtonText && (
            <TextButton
              type="secondary"
              disabled={isSecondaryButtonDisabled}
              onClick={onSecondaryButtonClick}
              text={secondaryButtonText}
              title={secondaryButtonTitle}
              id="modal-secondary-action-button"
              testId="modal-secondary-action-button"
            />
          )}
          {!hideCancelButton && (
            <TextButton
              onClick={onCancelClick}
              classNames="cancel-button"
              type="text-only"
              text={cancelButtonText}
              id="modal-cancel-button"
              testId="modal-cancel-button"
            />
          )}
        </div>
      </div>
    </div>,
    container
  );
};

export default Modal;
